<script setup lang="ts">
  import Table from '@/components/table/Table.vue'
  import TableColumnSheet from '@/components/table/TableColumnSheet.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useTemplateStore } from '@/store/template.store'
  import { UiButton, type Column } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'

  const templateStore = useTemplateStore()
  const router = useRouter()

  const columns: Column[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Naam', searchable: true },
    { key: 'number', label: 'Template Nummer', searchable: true },
    { key: 'fromAddress.street2', label: 'Herkomst', searchable: true },
    { key: 'toAddress.street2', label: 'Bestemming', searchable: true },
    { key: 'actions', label: 'Acties', sticky: 'right' },
  ]

  const visibleColumns = ref<Column[]>([])
  const showColumnSheet = ref<boolean>(false)

  const errorMessage = ref<string | null>()

  const rows = computed(() =>
    templateStore.templates.map(template => ({
      ...template,
      number: template.bgb?.groupId ?? template.ecmr?.domainIdentifier ?? 'N/A',
    }))
  )

  onMounted(async () => {
    await fetchTemplates()
  })

  async function fetchTemplates() {
    try {
      await templateStore.fetchAll()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        errorMessage.value = error.response.data.message
      } else {
        throw error
      }
    }
  }
</script>

<template>
  <TopBar>
    <UiButton v-if="rows.length > 0" icon="more" @click="showColumnSheet = true" />

    <UiButton variant="prominent" icon="plus" @click="router.push({ name: 'template-new' })" />
  </TopBar>

  <TableColumnSheet
    v-model:show="showColumnSheet"
    v-model:visible-columns="visibleColumns"
    :columns="columns"
  />

  <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

  <div class="layout" v-else>
    <Table :rows="rows" :columns="visibleColumns" row-key="id">
      <template #cell-actions="{ row }">
        <UiButton
          variant="link"
          icon="edit"
          @click="router.push({ name: 'template-edit', params: { templateId: row.id } })"
        />
      </template>
    </Table>
  </div>
</template>

<style lang="css" scoped>
  .error-message {
    color: var(--colours-support-negative);
  }

  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-s);
    min-height: calc(100dvh - var(--top-bar-height));
  }
</style>
